import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Trash2, UserPlus, Eye } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Employee {
  id: string;
  email: string;
  role: string;
}

type AccountType = 'admin' | 'moderator' | 'user';

const AdminEmployees = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [accountType, setAccountType] = useState<AccountType>('moderator');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch employees (moderators)
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('list-employees');

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Filter to show only employees (not regular users)
      return (data.employees as Employee[]).filter(e =>
        e.role === 'admin' || e.role === 'moderator'
      );
    }
  });

  // Fetch user details
  const { data: userDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['user-details', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;

      const { data, error } = await supabase.functions.invoke('get-user-details', {
        body: { userId: selectedUserId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    enabled: !!selectedUserId
  });

  // Create account mutation (employee or user)
  const createAccount = useMutation({
    mutationFn: async ({ email, password, type, first_name, last_name, employee_number }: { email: string; password: string; type: AccountType; first_name: string; last_name: string; employee_number: string }) => {
      const functionName = type === 'user' ? 'create-user' : 'create-employee';
      const body = type === 'user'
        ? { email, password, first_name, last_name }
        : { email, password, role: type, first_name, last_name, employee_number };
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setEmail("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setEmployeeNumber("");
      toast({
        title: accountType === 'user' ? "Utilisateur créé" : "Employé créé",
        description: `Le compte a été créé avec succès`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer le compte",
        variant: "destructive",
      });
    }
  });

  // Delete employee mutation
  const deleteEmployee = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-employee', {
        body: { userId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast({
        title: "Employé supprimé",
        description: "Le compte employé a été supprimé",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'employé",
        variant: "destructive",
      });
    }
  });

  // Update role mutation
  const updateRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: string }) => {
      const { data, error } = await supabase.functions.invoke('update-employee-role', {
        body: { userId, newRole }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    onMutate: async ({ userId, newRole }) => {
      // Cancel any in-flight refetch so it doesn't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['employees'] });
      const previous = queryClient.getQueryData<Employee[]>(['employees']);
      queryClient.setQueryData<Employee[]>(['employees'], (old) =>
        old ? old.map((e) => (e.id === userId ? { ...e, role: newRole } : e)) : old
      );
      return { previous };
    },
    onSuccess: () => {
      toast({
        title: "Rôle modifié",
        description: "Le rôle de l'employé a été modifié avec succès",
      });
    },
    onError: (error: any, _vars, context) => {
      // Rollback
      if (context?.previous) {
        queryClient.setQueryData(['employees'], context.previous);
      }
      toast({
        title: "Erreur",
        description: error.message || "Impossible de modifier le rôle",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive",
      });
      return;
    }
    createAccount.mutate({ email, password, type: accountType, first_name: firstName, last_name: lastName, employee_number: employeeNumber });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">Gestion des employés</h1>

      <Alert className="mb-6">
        <AlertDescription>
          <strong>Rôles disponibles :</strong>
          <ul className="mt-2 ml-4 list-disc space-y-1">
            <li><strong>Administrateur</strong> : Accès complet (produits, commandes, employés, clients, contenu et messages)</li>
            <li><strong>Modérateur</strong> : Peut gérer les blogs, projets, événements et messages de contact</li>
          </ul>
        </AlertDescription>
      </Alert>


      {/* Create account form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Créer un compte</CardTitle>
          <CardDescription>
            Créez un compte employé ou utilisateur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="accountType">Type de compte</Label>
              <Select value={accountType} onValueChange={(value) => setAccountType(value as AccountType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="moderator">Modérateur</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jean"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Tremblay"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="employeeNumber">Numéro d'employé</Label>
              <Input
                id="employeeNumber"
                value={employeeNumber}
                onChange={(e) => setEmployeeNumber(e.target.value)}
                placeholder="EMP-001"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="employe@exemple.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                minLength={6}
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={createAccount.isPending}
              className="w-full sm:w-auto"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {createAccount.isPending ? "Création..." : "Créer le compte"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Employees list (admin & moderator) */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Employés actifs</CardTitle>
          <CardDescription>
            {employees.length} employé{employees.length > 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : employees.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Aucun employé pour le moment
            </p>
          ) : (
            <div className="space-y-3">
              {employees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedUserId(employee.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={employee.email}>{employee.email}</p>
                      <p className="text-sm text-muted-foreground">
                        {employee.role === 'admin'
                          ? 'Administrateur (tous les accès)'
                          : 'Modérateur (blogs, projets, événements, messages)'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedUserId(employee.id)}
                        title="Voir les détails"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Select
                        value={employee.role}
                        onValueChange={(newRole) => updateRole.mutate({ userId: employee.id, newRole })}
                        disabled={updateRole.isPending}
                      >
                        <SelectTrigger className="w-[170px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-50">
                          <SelectItem value="admin">Administrateur</SelectItem>
                          <SelectItem value="moderator">Modérateur</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteEmployee.mutate(employee.id)}
                        disabled={deleteEmployee.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

            </div>
          )}
        </CardContent>
      </Card>


      {/* User details modal */}
      <Dialog open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId(null)}>
        <DialogContent className="max-w-2xl bg-background">
          <DialogHeader>
            <DialogTitle>Détails de l'employé</DialogTitle>
            <DialogDescription>
              Informations du compte employé
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingDetails ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : userDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Numéro d'employé</Label>
                  <p className="font-medium">{userDetails.profile?.employee_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rôle</Label>
                  <p className="font-medium">
                    {userDetails.role === 'admin' ? 'Administrateur' :
                     userDetails.role === 'moderator' ? 'Modérateur' : 'Utilisateur'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Prénom</Label>
                  <p className="font-medium">{userDetails.profile?.first_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Nom</Label>
                  <p className="font-medium">{userDetails.profile?.last_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium break-all">{userDetails.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p className="font-medium">{userDetails.profile?.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Compte créé le</Label>
                  <p className="font-medium">
                    {userDetails.created_at
                      ? new Date(userDetails.created_at).toLocaleDateString('fr-CA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Dernière connexion</Label>
                  <p className="font-medium">
                    {userDetails.last_sign_in_at
                      ? new Date(userDetails.last_sign_in_at).toLocaleDateString('fr-CA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployees;
